"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { track, type PageType } from "@/lib/directory-tracking";
import { isCategorySlug } from "@/lib/directorio/routes-static";

// Emite un `page_view` por navegación. Antes esto se delegaba a GA4,
// pero sin un page_view propio no hay denominador para ningún
// porcentaje por canal: no se puede decir "la publicidad trae X % del
// tráfico" sin contar el tráfico en la misma base donde está la
// atribución.
//
// Se monta junto a HealthTracking dentro de StorefrontOnly, así que no
// corre en los paneles de gestión ni de comercio.

function esRutaDePagina(path: string): boolean {
  if (!path) return false;
  return !(
    path.startsWith("/_next/") ||
    path.startsWith("/api/") ||
    /\.(png|jpe?g|webp|avif|gif|svg|ico|css|js|map|txt|xml|woff2?)$/i.test(path)
  );
}

/**
 * Clasifica la ruta para poder agregar server-side sin parsear paths en
 * cada query. Hasta ahora `pageType` llegaba NULL en el 99 % de las
 * filas, lo que dejaba inútil el índice `(pageType, timestamp)`.
 *
 * Estructura de rutas del directorio:
 *   /                          → home
 *   /{rubro}                   → categoria   (vocabulario controlado)
 *   /{partido}                 → partido
 *   /{partido}/{rubro}         → categoria
 *   /{partido}/{localidad}     → localidad
 *   /{partido}/{comercio}      → ficha
 *   /{partido}/{loc}/{rubro}   → categoria
 */
export function classifyPageType(pathname: string): PageType {
  const segs = pathname.split("/").filter(Boolean);
  if (segs.length === 0) return "home";
  if (segs.length === 1) return isCategorySlug(segs[0]) ? "categoria" : "partido";
  if (segs.length === 2) {
    if (isCategorySlug(segs[1])) return "categoria";
    // Localidad vs ficha de comercio no se puede distinguir solo por la
    // forma del path (ambos son `/{partido}/{slug}`). El servidor sí lo
    // sabe, pero acá alcanza con marcar el nivel: las fichas se
    // identifican de todas formas por `comercioId`, que sí viaja en los
    // eventos de la ficha.
    return "localidad";
  }
  if (segs.length === 3 && isCategorySlug(segs[2])) return "categoria";
  return "other";
}

export default function PageViewTracking(): null {
  const pathname = usePathname();

  useEffect(() => {
    if (!pathname || !esRutaDePagina(pathname)) return;
    track("page_view", {
      pageType: classifyPageType(pathname),
      path: pathname,
      // Una navegación puede re-montar el componente; el dedup de 5 s
      // por (eventType, dedupKey) evita contar dos veces la misma vista.
      dedupKey: pathname,
    });
  }, [pathname]);

  return null;
}
